import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import { formatMoney } from '../sponsor-pricing/sponsorPricingFormatters';
import {
  completeEnterpriseSetupApi,
  createEnterpriseSetupApi,
  updateEnterpriseSetupApi,
  createStripeCheckoutApi,
  getEnterpriseInviteApi,
  getEnterpriseSetupApi,
  getEnterpriseSetupStatusApi,
  getPricingPreviewApi,
  getPricingTableApi,
  getReferralPreviewApi,
  submitAchBillingApi,
  uploadRosterApi,
} from '../../api/enterpriseSetup.api';

const STEPS = [
  'Organization',
  'Group Type',
  'Billing Contact',
  'Roster',
  'Group Info',
  'Payment',
];

function ChecklistItem({ label, complete }) {
  return (
    <div className={`es-check-item ${complete ? 'es-check-done' : ''}`}>
      <i className={`fas ${complete ? 'fa-circle-check' : 'fa-circle'}`} />
      <span>{label}</span>
    </div>
  );
}

export default function EnterpriseSetupWizard() {
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState(null);
  const [status, setStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [referralPreview, setReferralPreview] = useState(null);
  const [invite, setInvite] = useState(null);
  const [pricingTable, setPricingTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [orgType, setOrgType] = useState('sponsor');
  const [form, setForm] = useState({
    groupName: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    expectedAssociates: '',
    onboardingContactName: '',
    onboardingContactEmail: '',
    onboardingContactPhone: '',
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: '',
    billingContactTitle: '',
    bankRoutingNumber: '',
    bankAccountNumber: '',
  });

  const isReferral = orgType === 'referral' || org?.orgType === 'referral';

  async function refresh(orgId = org?.id) {
    if (!orgId) return;
    const [statusRes, inviteRes] = await Promise.all([
      apiHandler(() => getEnterpriseSetupStatusApi(orgId)),
      apiHandler(() => getEnterpriseInviteApi(orgId)),
    ]);
    setStatus(unwrapData(statusRes));
    setInvite(unwrapData(inviteRes));

    if (isReferral || org?.orgType === 'referral') {
      const refRes = await apiHandler(() => getReferralPreviewApi(orgId));
      setReferralPreview(unwrapData(refRes));
    } else {
      const priceRes = await apiHandler(() => getPricingPreviewApi(orgId));
      setPricing(unwrapData(priceRes));
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      const existing = await apiHandler(() => getEnterpriseSetupApi());
      const data = unwrapData(existing);
      if (data) {
        setOrg(data);
        setOrgType(data.orgType || 'sponsor');
        await refresh(data.id);
      }
      setLoading(false);
    })();
  }, []);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCreateOrg() {
    setSaving(true);
    const payload = {
      ...form,
      orgType,
      expectedAssociates: form.expectedAssociates ? Number(form.expectedAssociates) : undefined,
    };
    const result = org?.id
      ? await apiHandler(() => updateEnterpriseSetupApi(org.id, payload))
      : await apiHandler(() => createEnterpriseSetupApi(payload));
    if (result.success) {
      const data = unwrapData(result);
      setOrg(data);
      setMessage('Organization setup saved');
      await refresh(data.id);
      setStep(2);
    } else {
      setMessage(result.message || 'Failed to save organization');
    }
    setSaving(false);
  }

  async function handleBillingContact() {
    if (!org?.id) return;
    setSaving(true);
    const result = await apiHandler(() => updateEnterpriseSetupApi(org.id, {
      billingContactName: form.billingContactName,
      billingContactTitle: form.billingContactTitle,
      billingContactEmail: form.billingContactEmail,
      billingContactPhone: form.billingContactPhone,
    }));
    if (result.success) {
      setMessage('Billing contact saved');
      await refresh(org.id);
      setStep(3);
    } else {
      setMessage(result.message || 'Failed to save billing contact');
    }
    setSaving(false);
  }

  async function handleRosterUpload(file) {
    if (!org?.id || !file) return;
    setSaving(true);
    const result = await apiHandler(() => uploadRosterApi(org.id, file));
    if (result.success) {
      setMessage('Roster uploaded');
      await refresh(org.id);
    } else {
      setMessage(result.message || 'Roster upload failed');
    }
    setSaving(false);
  }

  async function handleCompleteSetup() {
    if (!org?.id) return;
    setSaving(true);
    const result = await apiHandler(() => completeEnterpriseSetupApi(org.id));
    if (result.success) {
      const data = unwrapData(result);
      setMessage(data.nextStep === 'active' ? 'Referral group activated' : 'Proceed to payment');
      await refresh(org.id);
      if (data.nextStep === 'billing') setStep(5);
    } else {
      setMessage(result.message || 'Setup incomplete');
    }
    setSaving(false);
  }

  async function handleAchSubmit() {
    if (!org?.id) return;
    setSaving(true);
    const result = await apiHandler(() => submitAchBillingApi(org.id, {
      bankRoutingNumber: form.bankRoutingNumber,
      bankAccountNumber: form.bankAccountNumber,
      authorized: true,
    }));
    if (result.success) {
      setMessage('ACH submitted — pending review');
      await refresh(org.id);
    } else {
      setMessage(result.message || 'ACH submission failed');
    }
    setSaving(false);
  }

  async function handleStripeCheckout() {
    if (!org?.id) return;
    setSaving(true);
    const result = await apiHandler(() => createStripeCheckoutApi(org.id, {
      clientPlatform: 'web',
      successUrl: `${window.location.origin}/enterprise-setup?paid=1`,
      cancelUrl: `${window.location.origin}/enterprise-setup?cancel=1`,
    }));
    if (result.success) {
      const data = unwrapData(result);
      if (data.checkoutUrl) window.location.href = data.checkoutUrl;
    } else {
      setMessage(result.message || 'Checkout failed');
    }
    setSaving(false);
  }

  async function openPricingTable() {
    const result = await apiHandler(() => getPricingTableApi());
    if (result.success) setPricingTable(unwrapData(result));
  }

  if (loading) {
    return <div className="page-container"><p>Loading enterprise setup…</p></div>;
  }

  return (
    <div className="page-container es-wizard">
      <PageHeader
        title="Organization / Enterprise Setup"
        subtitle="Figma-aligned owner setup wizard (requires web user token in localStorage)"
      />

      {message && (
        <div className="es-banner">{message}</div>
      )}

      <div className="es-layout">
        <aside className="es-sidebar">
          <h3>Checklist</h3>
          {status?.checklist ? (
            <>
              <ChecklistItem label="Billing contact" complete={status.checklist.billingContact?.complete} />
              <ChecklistItem label="Onboarding contact" complete={status.checklist.onboardingContact?.complete} />
              <ChecklistItem label="Roster uploaded" complete={status.checklist.rosterUploaded?.complete} />
              <ChecklistItem label="Template downloaded" complete={status.checklist.templateDownloaded?.complete} />
              <ChecklistItem label={isReferral ? 'Referral configured' : 'Pricing configured'} complete={isReferral ? status.checklist.referralConfigured?.complete : status.checklist.sponsorPricingConfigured?.complete} />
            </>
          ) : (
            <p className="es-muted">Start setup to see checklist</p>
          )}
          <div className="es-step-nav">
            {STEPS.map((label, idx) => (
              <button
                key={label}
                type="button"
                className={`es-step-btn ${step === idx ? 'active' : ''}`}
                onClick={() => setStep(idx)}
              >
                {idx + 1}. {label}
              </button>
            ))}
          </div>
        </aside>

        <main className="es-main">
          {step === 0 && (
            <section className="es-panel">
              <h2>Organization Information</h2>
              <div className="es-form-grid">
                {['groupName', 'company', 'address', 'city', 'state', 'zipCode', 'website', 'expectedAssociates', 'onboardingContactName', 'onboardingContactEmail', 'onboardingContactPhone'].map((key) => (
                  <label key={key} className="es-field">
                    <span>{key}</span>
                    <input value={form[key]} onChange={(e) => updateField(key, e.target.value)} />
                  </label>
                ))}
              </div>
              <button type="button" className="action-btn btn-blue" onClick={() => setStep(1)}>Continue</button>
            </section>
          )}

          {step === 1 && (
            <section className="es-panel">
              <h2>Select Group Type</h2>
              <div className="es-type-cards">
                <button type="button" className={`es-type-card ${orgType === 'sponsor' ? 'selected' : ''}`} onClick={() => setOrgType('sponsor')}>
                  <h3>Sponsor Group</h3>
                  <p>Organization pays for associates. CSV roster required.</p>
                </button>
                <button type="button" className={`es-type-card ${orgType === 'referral' ? 'selected' : ''}`} onClick={() => setOrgType('referral')}>
                  <h3>Referral Group</h3>
                  <p>Members pay with discount. Revenue share for owner.</p>
                </button>
              </div>
              <button type="button" className="action-btn btn-blue" disabled={saving} onClick={handleCreateOrg}>
                {saving ? 'Saving…' : 'Continue Organization Setup'}
              </button>
            </section>
          )}

          {step === 2 && (
            <section className="es-panel">
              <h2>Billing Contact</h2>
              <div className="es-form-grid">
                {['billingContactName', 'billingContactTitle', 'billingContactEmail', 'billingContactPhone'].map((key) => (
                  <label key={key} className="es-field">
                    <span>{key}</span>
                    <input value={form[key]} onChange={(e) => updateField(key, e.target.value)} />
                  </label>
                ))}
              </div>
              <button type="button" className="action-btn btn-blue" disabled={saving} onClick={handleBillingContact}>Save &amp; Continue</button>
            </section>
          )}

          {step === 3 && (
            <section className="es-panel">
              <h2>Roster Template</h2>
              <p className="es-muted">{isReferral ? 'Optional for referral groups.' : 'Required for sponsor groups before activation.'}</p>
              <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => handleRosterUpload(e.target.files?.[0])} />
              {status?.csvUploaded && <p className="es-success">CSV uploaded ({status.expectedAssociates?.value} associates)</p>}
              <button type="button" className="action-btn btn-blue" onClick={() => setStep(4)}>Continue</button>
            </section>
          )}

          {step === 4 && (
            <section className="es-panel">
              <h2>{isReferral ? 'Referral Member Offer' : 'Sponsor Group Information'}</h2>
              {isReferral && referralPreview ? (
                <div className="es-info-card">
                  <p><strong>Discount:</strong> {referralPreview.discountOfferedToMembers}%</p>
                  <p><strong>Member price:</strong> {referralPreview.priceDisplay}</p>
                  <p><strong>Base price:</strong> {referralPreview.basePriceDisplay}</p>
                  <p><strong>Revenue share:</strong> {referralPreview.groupShareLabel}</p>
                  <p><strong>Payout:</strong> {referralPreview.payoutFrequencyLabel} → {referralPreview.payoutDestination}</p>
                </div>
              ) : pricing ? (
                <div className="es-info-card">
                  <p><strong>Advisor:</strong> {pricing.sponsorGroupAdvisor}</p>
                  <p><strong>Tier:</strong> {pricing.tierLabel}</p>
                  <p><strong>Price / associate:</strong> {formatMoney(pricing.defaultPricePerAssociate)}</p>
                  <p><strong>Expected associates:</strong> {pricing.expectedAssociates}</p>
                  <p><strong>Annual total:</strong> {formatMoney(pricing.estimatedAnnualTotal)}</p>
                  <button type="button" className="action-btn" onClick={openPricingTable}>Check Pricing Table</button>
                </div>
              ) : null}

              {invite && (
                <div className="es-invite-card">
                  <h3>Group Code (Share to Invite)</h3>
                  <p><strong>Code:</strong> {invite.groupCode}</p>
                  <p><strong>URL:</strong> {invite.inviteUrl}</p>
                  {invite.qrCodeDataUrl && (
                    <img src={invite.qrCodeDataUrl} alt="Group invite QR code" className="es-qr" />
                  )}
                </div>
              )}

              <button type="button" className="action-btn btn-blue" disabled={saving} onClick={handleCompleteSetup}>
                {isReferral ? 'Activate Referral Group' : 'Continue to Payment'}
              </button>
            </section>
          )}

          {step === 5 && !isReferral && (
            <section className="es-panel">
              <h2>Payment</h2>
              <p className="es-muted">Primary: ACH. Secondary: Stripe (+3.1% fee).</p>
              <div className="es-form-grid">
                <label className="es-field"><span>Routing number</span><input value={form.bankRoutingNumber} onChange={(e) => updateField('bankRoutingNumber', e.target.value)} /></label>
                <label className="es-field"><span>Account number</span><input value={form.bankAccountNumber} onChange={(e) => updateField('bankAccountNumber', e.target.value)} /></label>
              </div>
              <div className="es-action-row">
                <button type="button" className="action-btn btn-green" disabled={saving} onClick={handleAchSubmit}>Submit ACH</button>
                <button type="button" className="action-btn btn-blue" disabled={saving} onClick={handleStripeCheckout}>Pay with Stripe</button>
              </div>
              {status?.setupStatus === 'pending_review' && (
                <p className="es-success">Payment successful — pending admin review.</p>
              )}
            </section>
          )}

          {pricingTable && (
            <div className="es-modal">
              <div className="es-modal-body">
                <h3>Pricing Table</h3>
                <pre>{JSON.stringify(pricingTable, null, 2)}</pre>
                <button type="button" onClick={() => setPricingTable(null)}>Close</button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
